/**
 * Renders the `persistence` block a verify returns — the one place the
 * reviewer learns where the experience went:
 *
 *   { persons[], entities[], queued[] }  linked / created on the canonical records;
 *                                        queued = a near-match that needs a
 *                                        human to confirm identity (NOT persisted)
 *   { skipped }                          no borrower / entity parent → persisted nowhere
 *   { withdrawnFrom }                    un-verify pulled it back off N records
 *   { error }                            the registry call failed (the deal rollup still ran)
 */
import PropTypes from "prop-types";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";

const count = (rows, action) => (rows || []).filter((r) => r?.action === action).length;

const PersistenceNotice = ({ verified, result }) => {
  if (!result) return null;

  if (result.error)
    return (
      <Alert severity="error" sx={{ fontSize: 12.5 }}>
        The deal rollup ran, but the shared record could not be written: {String(result.error)}
      </Alert>
    );

  if (result.skipped)
    return (
      <Alert severity="warning" sx={{ fontSize: 12.5 }}>
        Verified on the deal, but persisted nowhere — {String(result.skipped)}. Attach the
        borrower, entity or contractor it belongs to and verify again.
      </Alert>
    );

  if (!verified || result.withdrawnFrom != null)
    return (
      <Alert severity="info" sx={{ fontSize: 12.5 }}>
        Withdrawn from {result.withdrawnFrom ?? 0} shared record{result.withdrawnFrom === 1 ? "" : "s"} —
        un-verifying never leaves stale experience behind.
      </Alert>
    );

  const persons = result.persons || [];
  const entities = result.entities || [];
  const queued = result.queued || [];
  const linked = count(persons, "updated") + count(entities, "updated");
  const created =
    persons.filter((r) => r?.status === "created").length + entities.filter((r) => r?.status === "created").length;

  return (
    <Flx column g={1}>
      {persons.length || entities.length ? (
        <Alert severity="success" sx={{ fontSize: 12.5 }}>
          Saved to the shared record — {persons.length} person{persons.length === 1 ? "" : "s"},{" "}
          {entities.length} entit{entities.length === 1 ? "y" : "ies"}
          {created ? ` · ${created} new canonical record${created === 1 ? "" : "s"} minted` : ""}
          {linked ? ` · ${linked} updated` : ""}. A returning borrower carries this experience to their next loan.
        </Alert>
      ) : null}
      {queued.map((q, i) => (
        <Alert key={i} severity="warning" sx={{ fontSize: 12.5 }}>
          <Txt sx={{ fontSize: 12.5 }}>
            <b>{q?.name || "This party"}</b> closely matches <b>{q?.closestName || "an existing record"}</b> —
            confirm identity before this experience is saved to their record.
          </Txt>
          <Box sx={{ fontSize: 11.5, mt: 0.5, opacity: 0.85 }}>
            {q?.kind} · confidence {q?.confidence != null ? `${Math.round(Number(q.confidence) * 100)}%` : "—"}
            {q?.basis ? ` · ${q.basis}` : ""} · the deal rollup still counts this item
          </Box>
        </Alert>
      ))}
    </Flx>
  );
};

PersistenceNotice.propTypes = { verified: PropTypes.bool, result: PropTypes.object };

export default PersistenceNotice;
